import type {
  CustomerImportDraft,
  CustomerImportReviewGroupSummary,
  CustomerImportReviewItem,
  CustomerImportReviewSummary,
} from "../../../../../../../api/kehrblatt/imports"

export type ImportReviewTone = "critical" | "recommended" | "info"

export type ImportDashboardNumber = {
  key: string
  label: string
  value: number | string | null
  tooltipContent?: string | null
}

export type ImportReviewGroupView = CustomerImportReviewGroupSummary & {
  tone: ImportReviewTone
  title: string
  description: string
}

export type ImportReviewItemFact = {
  label: string
  value: string
}

const GROUP_COPY: Record<string, { title: string; description: string }> = {
  possible_duplicates: {
    title: "Mögliche Dubletten",
    description: "Ähnliche Ansprechpartner wurden erkannt, aber nicht automatisch zusammengeführt.",
  },
  missing_from_export: {
    title: "Nicht automatisch entfernt",
    description:
      "KehrBlatt entfernt fehlende Importdaten automatisch. Hier erscheinen nur Fälle, die wegen offener Vorgänge nicht entfernt werden konnten.",
  },
  manual_values_protected: {
    title: "Geschützte manuelle Daten",
    description:
      "Manuell gepflegte oder geprüfte Werte wurden durch den Import nicht überschrieben.",
  },
  missing_required_data: {
    title: "Fehlende Pflichtdaten",
    description: "Einige Datensätze enthalten zu wenig Informationen für eine sichere Zuordnung.",
  },
  low_quality_no_action: {
    title: "Schwache Datenqualität",
    description:
      "Namen oder Angaben, die Kehrblatt sicherheitshalber nicht in Nachrichten verwendet.",
  },
}

const AUTOMATIC_CLEANUP_COUNT_KEYS = [
  "missing_property_customer_deleted",
  "missing_contact_point_deleted",
  "missing_customer_archived",
  "missing_unit_archived",
  "missing_unit_inactive_with_archived_property",
  "missing_property_archived",
]

const automaticCleanupCount = (
  result: CustomerImportDraft["result_snapshot"] | null | undefined,
) => {
  const counts = ((result as Record<string, unknown> | null | undefined)?.reimport_diff_counts ??
    {}) as Record<string, unknown>

  return AUTOMATIC_CLEANUP_COUNT_KEYS.reduce((sum, key) => {
    const value = counts[key]

    return sum + (typeof value === "number" && Number.isFinite(value) ? value : 0)
  }, 0)
}

export const importReviewToneForSeverity = (severity?: string | null): ImportReviewTone => {
  if (severity === "critical" || severity === "high") return "critical"

  if (severity === "medium") return "recommended"

  return "info"
}

export const buildImportDashboardNumbers = (
  draft: CustomerImportDraft | null,
): ImportDashboardNumber[] => {
  const preview = draft?.preview_summary
  const result = draft?.result_snapshot

  return [
    {
      key: "import-completed",
      label: "Import abgeschlossen",
      value: draft?.status === "committed" ? "Ja" : "Offen",
      tooltipContent: draft?.source_file_name ?? null,
    },
    {
      key: "records-processed",
      label: "Datensätze verarbeitet",
      value: preview?.record_count ?? 0,
    },
    {
      key: "records-reused",
      label: "Wiederverwendet",
      value: result?.reused ?? 0,
      tooltipContent: "Bestehende Daten erkannt.",
    },
    {
      key: "records-created",
      label: "Neu angelegt",
      value: result?.created ?? 0,
      tooltipContent: "Ansprechpartner, Liegenschaften oder Kontaktwege.",
    },
    {
      key: "automatic-cleanup",
      label: "Automatisch bereinigt",
      value: automaticCleanupCount(result),
      tooltipContent:
        "Fehlende importierte Liegenschaften, Einheiten oder Ansprechpartner wurden archiviert; unabhängige Verknüpfungen und Kontaktwege wurden entfernt.",
    },
  ]
}

export const projectImportReviewGroups = (
  summary: CustomerImportReviewSummary | null,
): ImportReviewGroupView[] =>
  (summary?.groups ?? [])
    .filter((group) => group.total_count > 0)
    .map((group) => {
      const copy = GROUP_COPY[group.group_key] ?? {
        title: group.label,
        description: "Diese Hinweise wurden beim Import erkannt.",
      }

      return {
        ...group,
        title: copy.title,
        description: copy.description,
        tone: importReviewToneForSeverity(group.severity_max),
      }
    })

const firstText = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()
  }

  return null
}

export const entityRefText = (item: CustomerImportReviewItem, key: string): string => {
  const refs = item.entity_refs ?? {}
  const payload = item.payload ?? {}
  const ref = refs[key]
  const refRecord = typeof ref === "object" && ref !== null ? (ref as Record<string, unknown>) : {}

  return (
    firstText(
      refRecord.label,
      refRecord.display_label,
      refRecord.name,
      payload[`${key}_label`],
      payload[`${key}_name`],
      payload[`${key}_summary`],
    ) ?? "-"
  )
}

export const contactPointText = (item: CustomerImportReviewItem): string => {
  const payload = item.payload ?? {}

  return (
    firstText(payload.contact_point_label, payload.contact_value, payload.email, payload.phone) ??
    "-"
  )
}

export const sourceText = (item: CustomerImportReviewItem): string =>
  firstText(item.payload?.source_label, item.source_summary, item.source_record_key) ?? "-"

export const roleText = (item: CustomerImportReviewItem): string => {
  const roleRef = entityRefText(item, "role")

  return (
    firstText(item.payload?.role_label, roleRef === "-" ? null : roleRef, item.payload?.role) ?? "-"
  )
}

const shorten = (value: string, maxLength = 120): string => {
  const normalized = value.replace(/\s+/g, " ").trim()

  if (normalized.length <= maxLength) return normalized

  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`
}

export const reviewStatusLabel = (status?: string | null): string => {
  if (status === "open") return "Offen"

  if (status === "resolved") return "Erledigt"

  if (status === "ignored") return "Ignoriert"

  if (status === "auto_resolved") return "Automatisch erledigt"

  return status ? "Status unbekannt" : "-"
}

export const reviewSeverityLabel = (severity?: string | null): string => {
  if (severity === "critical") return "Kritisch"

  if (severity === "high") return "Hoch"

  if (severity === "medium") return "Mittel"

  if (severity === "low") return "Niedrig"

  if (severity === "info") return "Info"

  return severity ? "Schwere unbekannt" : "-"
}

export const reviewActionLabel = (action: string): string => {
  if (action === "ignore") return "Ignorieren"

  if (action === "mark_resolved") return "Erledigt"

  if (action === "reopen") return "Wieder öffnen"

  return "Aktion"
}

export const reviewItemPreviewText = (item: CustomerImportReviewItem): string =>
  shorten(
    firstText(item.summary, item.recommended_action, item.title) ?? "Hinweis aus der Datenprüfung.",
    96,
  )

export const reviewItemFacts = (item: CustomerImportReviewItem): ImportReviewItemFact[] =>
  [
    { label: "Status", value: reviewStatusLabel(item.status) },
    { label: "Schwere", value: reviewSeverityLabel(item.severity) },
    { label: "Liegenschaft", value: entityRefText(item, "property") },
    { label: "Ansprechpartner", value: entityRefText(item, "customer") },
    { label: "Rolle", value: roleText(item) },
    { label: "Kontaktweg", value: contactPointText(item) },
    { label: "Quelle", value: sourceText(item) },
    { label: "Empfehlung", value: firstText(item.recommended_action) ?? "-" },
  ].filter((fact) => fact.value !== "-")

export const canContinueImportOnboarding = (summary: CustomerImportReviewSummary | null): boolean =>
  (summary?.counts_by_severity?.critical ?? 0) === 0
